import type { MessageTemplateView } from "@vaulted/shared";
import { MESSAGE_PLACEHOLDERS } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, get, post, put } from "../../api";
import { Alert, Badge, Button, Checkbox, Dialog, Field, Loading, Panel, PanelHeader, TextAreaField } from "../../components/ui";
import { SettingsForm, type SettingsResponse, useDraft } from "./common";

const KEYS = ["messages.emailFrom", "messages.quietStart", "messages.quietEnd"] as const;

/** Client texts and emails: sender, quiet hours, and each message's wording (on or off per channel). */
export function MessagesTab({ data }: { data: SettingsResponse }) {
  const draft = useDraft(data.settings, KEYS, "Saved.");
  const v = draft.value;
  const list = useQuery({ queryKey: ["message-templates"], queryFn: () => get<MessageTemplateView[]>("/api/message-templates") });
  const [editing, setEditing] = useState<MessageTemplateView | null>(null);
  return (
    <div className="flex flex-col gap-6">
      <Panel>
        <PanelHeader
          title="Sending"
          description={`Texts come from the clinic's RingCentral number. Email comes from the address below (its domain must be set up in Amazon SES).${data.replyDomain ? ` Use an address @${data.replyDomain} and clients' replies come back into Vaulted (Communication, and their profile).` : ""}`}
        />
        <SettingsForm onSave={draft.save} saving={draft.saving} error={draft.error} notice={draft.notice} dirty={draft.dirty}>
          <Field label="Email from" type="email" placeholder={data.replyDomain ? `staff@${data.replyDomain}` : "staff@yourclinic.com"} className="max-w-md" value={v["messages.emailFrom"] ?? ""} onChange={(e) => draft.set("messages.emailFrom", e.target.value.trim() || null)} hint="Leave empty to send texts only." />
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Quiet hours start" type="time" value={v["messages.quietStart"]} onChange={(e) => e.target.value && draft.set("messages.quietStart", e.target.value)} />
            <Field label="Quiet hours end" type="time" value={v["messages.quietEnd"]} onChange={(e) => e.target.value && draft.set("messages.quietEnd", e.target.value)} />
          </div>
          <p className="text-sm text-muted">Reminders that would go out during quiet hours are sent just before they start instead. Messages answering something the client just did (booking, receipt) go right away.</p>
        </SettingsForm>
      </Panel>
      <Panel>
        <PanelHeader title="Messages" description="What clients get, and when. Turn each on or off by text and email, and edit the wording." />
        {list.isLoading ? (
          <Loading />
        ) : (
          <ul className="px-5 pb-3">
            {(list.data ?? []).map((t) => (
              <li key={t.key} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-mist py-3 last:border-0">
                <span className="min-w-0 flex-1">
                  <span className="font-semibold">{t.label}</span>
                  <span className="block text-sm text-muted">{t.when}</span>
                </span>
                <Badge tone={t.smsEnabled ? "ok" : "off"}>Text {t.smsEnabled ? "on" : "off"}</Badge>
                <Badge tone={t.emailEnabled ? "ok" : "off"}>Email {t.emailEnabled ? "on" : "off"}</Badge>
                <Button variant="quiet" onClick={() => setEditing(t)}>
                  Edit
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {editing && <TemplateDialog t={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function TemplateDialog({ t, onClose }: { t: MessageTemplateView; onClose: () => void }) {
  const qc = useQueryClient();
  const [v, setV] = useState({ smsEnabled: t.smsEnabled, emailEnabled: t.emailEnabled, smsText: t.smsText, emailSubject: t.emailSubject, emailBody: t.emailBody });
  const email = { emailSubject: v.emailSubject, emailBody: v.emailBody };
  const preview = useMutation({ mutationFn: () => post<{ subject: string; html: string }>(`/api/message-templates/${t.key}/preview`, email) });
  const test = useMutation({ mutationFn: () => post<{ to: string }>(`/api/message-templates/${t.key}/test`, email) });
  const save = useMutation({
    mutationFn: () => put(`/api/message-templates/${t.key}`, v),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["message-templates"] });
      onClose();
    },
  });
  return (
    <Dialog
      title={t.label}
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted">{t.when}</p>
        <Checkbox label="Send by text" checked={v.smsEnabled} onChange={(on) => setV({ ...v, smsEnabled: on })} />
        <TextAreaField label={`Text (${v.smsText.length} characters)`} rows={3} value={v.smsText} onChange={(e) => setV({ ...v, smsText: e.target.value })} />
        <Checkbox label="Send by email" checked={v.emailEnabled} onChange={(on) => setV({ ...v, emailEnabled: on })} />
        <Field label="Email subject" value={v.emailSubject} onChange={(e) => setV({ ...v, emailSubject: e.target.value })} />
        <TextAreaField label="Email" rows={8} value={v.emailBody} onChange={(e) => setV({ ...v, emailBody: e.target.value })} />
        <div className="flex flex-wrap items-center gap-2">
          <Button busy={preview.isPending} onClick={() => preview.mutate()}>
            {preview.data ? "Refresh preview" : "Preview email"}
          </Button>
          <Button variant="quiet" busy={test.isPending} onClick={() => test.mutate()}>
            Send me a test
          </Button>
          {test.data && <span className="text-sm text-muted">Sent to {test.data.to}.</span>}
        </div>
        {(preview.error || test.error) && <Alert>{errorMessage(preview.error ?? test.error)}</Alert>}
        {preview.data && (
          <div className="overflow-hidden rounded-[var(--radius-control)] border border-line">
            <p className="border-b border-line bg-mist/40 px-3 py-2 text-sm">
              <span className="text-muted">Subject:</span> {preview.data.subject}
            </p>
            {/* Made-up details; links in the preview go nowhere useful. */}
            <iframe title="Email preview" sandbox="" srcDoc={preview.data.html} className="h-[560px] w-full bg-white" />
          </div>
        )}
        <details className="text-sm">
          <summary className="cursor-pointer font-semibold text-muted">Placeholders</summary>
          <ul className="mt-1 grid gap-x-4 sm:grid-cols-2">
            {Object.entries(MESSAGE_PLACEHOLDERS).map(([k, d]) => (
              <li key={k}>
                <code>{`{${k}}`}</code> <span className="text-muted">{d}</span>
              </li>
            ))}
          </ul>
        </details>
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      </div>
    </Dialog>
  );
}
